import {
  activeContacts, advance, changeBand, cleanProfile, createShift, fileReport, finishShift,
  focusContact, KINDS, observe, position, REFERENCE,
  POWER_REGEN_SECONDS, SHIFT_SECONDS, START_POWER, VIEWS, VIEW_LABELS,
  type Band, type Contact, type Kind, type Shift,
} from './signal-lost-model.ts'
import { escapeHtml, profileSvg } from './signal-lost-view.ts'

function install(): void {
  const root = document.getElementById('signal-lost')
  if (!root) return
  const get = <T extends HTMLElement>(id: string): T => {
    const element = document.getElementById(id)
    if (!element) throw new Error(`Signal Lost: missing ${id}`)
    return element as T
  }
  const setup = get<HTMLFormElement>('sl-setup')
  const seed = get<HTMLInputElement>('sl-seed')
  const practiceInput = get<HTMLInputElement>('sl-practice')
  const board = get('sl-board')
  const contacts = get('sl-contacts')
  const scope = document.getElementById('sl-scope')!
  const profiles = get('sl-profiles')
  const reference = get<HTMLSelectElement>('sl-reference')
  const answer = get<HTMLSelectElement>('sl-answer')
  const confidence = get<HTMLSelectElement>('sl-confidence')
  const status = get('sl-status')
  const pause = get<HTMLButtonElement>('sl-pause')
  const wait = get<HTMLButtonElement>('sl-wait')
  const focus = get<HTMLButtonElement>('sl-focus')
  const band = get<HTMLButtonElement>('sl-band')
  const file = get<HTMLButtonElement>('sl-file')
  const unresolved = get<HTMLButtonElement>('sl-unresolved')
  const results = get('sl-results')
  let shift: Shift | null = null
  let selected = 0
  let practice = false
  let timer: number | null = null
  let lastTick = 0
  let profileKey = ''
  let referenceBand: Band = 'A'
  const buttons = new Map<number, HTMLButtonElement>()

  function stopClock(): void {
    if (timer !== null) window.clearInterval(timer)
    timer = null
  }

  function startClock(): void {
    stopClock()
    if (practice || shift?.status !== 'running' || document.hidden) return
    lastTick = performance.now()
    timer = window.setInterval(() => {
      if (!shift || shift.status !== 'running') return
      const now = performance.now()
      const previous = Math.floor(shift.elapsed)
      advance(shift, (now - lastTick) / 1000)
      lastTick = now
      if (Math.floor(shift.elapsed) !== previous || shift.elapsed >= SHIFT_SECONDS) render()
    }, 250)
  }

  function updateReferences(next: Band): void {
    if (referenceBand === next) return
    referenceBand = next
    get('sl-reference-band').textContent = `/ band ${next}`
    for (const kind of KINDS) {
      root!.querySelector(`[data-reference="${kind}"]`)!.innerHTML = VIEWS.map(view =>
        `<figure><figcaption>${VIEW_LABELS[view]}</figcaption>${profileSvg(cleanProfile(kind, view, next), VIEW_LABELS[view])}</figure>`).join('')
    }
  }

  function renderScope(active: Contact[]): void {
    if (!shift) return
    const grid = '<circle class="sl-grid" cx="160" cy="160" r="140"/><circle class="sl-grid" cx="160" cy="160" r="93"/><circle class="sl-grid" cx="160" cy="160" r="46"/><path class="sl-grid" d="M20 160H300 M160 20V300"/><text class="sl-compass" x="160" y="14" text-anchor="middle">N</text>'
    scope.innerHTML = grid + active.map(c => {
      const age = Math.floor(shift!.elapsed - c.arrives)
      const trail = Array.from({ length: Math.min(12, Math.floor(age / 2) + 1) }, (_, i) => position(c, Math.max(0, age - i * 2)))
      const p = position(c, age)
      const x = p.x * 320
      const y = p.y * 320
      return `<g class="${c.id === selected ? 'sl-selected' : ''}"><polyline class="sl-trail" points="${trail.map(t => `${(t.x * 320).toFixed(1)},${(t.y * 320).toFixed(1)}`).join(' ')}"/><circle class="sl-blip" cx="${x}" cy="${y}" r="4"/><text class="sl-contact-label" x="${x + 9}" y="${y - 8}">${c.id}</text></g>`
    }).join('')
  }

  function review(): void {
    if (!shift || !results.hidden) return
    stopClock()
    board.hidden = true
    results.hidden = false
    const correct = shift.contacts.filter(c => c.report?.answer === c.kind).length
    const wrong = shift.contacts.filter(c => c.report?.answer && c.report.answer !== c.kind).length
    const lost = shift.contacts.filter(c => c.report?.lost).length
    const points = shift.contacts.reduce((sum, c) => sum + (c.report?.points ?? 0), 0)
    get('sl-summary').textContent = `${points} points · ${correct} correct · ${wrong} wrong · ${lost} lost · ${9 - correct - wrong - lost} unresolved. Seed ${shift.seed}${practice ? ' · untimed practice' : ''}.`
    get('sl-reports').innerHTML = shift.contacts.map(c => {
      const report = c.report!
      const seen = report.at >= c.arrives
      const call = report.lost ? 'Lost before reporting' : report.answer ? `${REFERENCE[report.answer].name}, ${report.confidence}` : seen ? 'Unresolved' : 'Not observed'
      const observation = { ...shift!, elapsed: Math.min(report.at, c.leaves - 1) }
      return `<details><summary>Contact ${c.id}: ${REFERENCE[c.kind].name} <span>· ${report.points > 0 ? '+' : ''}${report.points}</span></summary>
        <p>Your report: ${escapeHtml(call)}. ${REFERENCE[c.kind].movement} ${REFERENCE[c.kind].signature}</p>
        ${seen ? `<p>Band ${c.band}, focus ${c.focus}/2. Received on the left, clean on the right.</p>${VIEWS.map(view => `<div class="sl-review-pair"><figure><figcaption>${VIEW_LABELS[view]} · received</figcaption>${profileSvg(observe(observation, c, view), 'Received ' + VIEW_LABELS[view])}</figure><figure><figcaption>${VIEW_LABELS[view]} · clean</figcaption>${profileSvg(cleanProfile(c.kind, view, c.band, c.variation, c.phase), 'Clean ' + VIEW_LABELS[view])}</figure></div>`).join('')}` : ''}
      </details>`
    }).join('')
    status.textContent = 'Shift complete. The contact identities are now available in the review.'
    get('sl-result-heading').focus()
  }

  function render(): void {
    if (!shift) return
    if (shift.status === 'finished') { review(); return }
    const active = activeContacts(shift)
    if (!active.some(c => c.id === selected)) {
      const lostSelection = selected !== 0
      selected = active[0]?.id ?? 0
      answer.value = ''
      confidence.value = 'tentative'
      if (lostSelection) status.textContent = selected ? `Now viewing contact ${selected}.` : 'No active contacts. Watch for the next return.'
    }
    const remaining = Math.ceil(SHIFT_SECONDS - shift.elapsed)
    get('sl-time').textContent = `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`
    get('sl-power').textContent = `${shift.power} / ${START_POWER}`
    get('sl-recharge').textContent = shift.power === START_POWER ? 'Full'
      : shift.status === 'paused' ? 'Recharge paused'
      : `+1 in ${Math.ceil(POWER_REGEN_SECONDS - shift.elapsed % POWER_REGEN_SECONDS)}s`
    get('sl-count').textContent = `${shift.contacts.filter(c => c.report).length} / 9`
    pause.textContent = shift.status === 'paused' ? 'Resume' : 'Pause'
    wait.hidden = !practice
    wait.disabled = shift.status !== 'running'
    for (const [id, button] of buttons) if (!active.some(c => c.id === id)) { button.remove(); buttons.delete(id) }
    for (const c of active) {
      let button = buttons.get(c.id)
      if (!button) {
        button = document.createElement('button')
        button.type = 'button'
        button.addEventListener('click', () => {
          selected = c.id
          answer.value = ''
          confidence.value = 'tentative'
          status.textContent = `Contact ${c.id} selected. Compare its return profiles below.`
          render()
        })
        buttons.set(c.id, button)
        contacts.append(button)
      }
      button.textContent = `Contact ${c.id} · ${Math.ceil(c.leaves - shift.elapsed)}s`
      button.setAttribute('aria-pressed', String(selected === c.id))
    }
    renderScope(active)
    const c = active.find(contact => contact.id === selected)
    const enabled = !!c && shift.status === 'running'
    focus.disabled = !enabled || shift.power < 2 || c!.focus >= 2
    band.disabled = !enabled || shift.power < 1
    for (const control of [file, unresolved, answer, confidence]) control.disabled = !enabled
    get('sl-contact-heading').textContent = c ? `Contact ${c.id} / returns` : 'Contact returns'
    if (!c) {
      const next = shift.contacts.find(contact => contact.arrives > shift!.elapsed)
      get('sl-track').textContent = next ? `Next contact in ${Math.ceil(next.arrives - shift.elapsed)} seconds.` : 'No contacts remain.'
      get('sl-evidence-meta').textContent = 'No contact selected.'
      profiles.replaceChildren()
      profileKey = ''
      return
    }
    const age = Math.floor(shift.elapsed - c.arrives)
    const p = position(c, age)
    const past = position(c, Math.max(0, age - 4))
    const range = Math.round(Math.hypot(p.x - 0.5, p.y - 0.5) * 200)
    const movement = Math.round(Math.hypot(p.x - past.x, p.y - past.y) * 100)
    get('sl-track').textContent = `Contact ${c.id}: range ${range}% of scope. Moved ${movement}% of scope width over the last ${Math.min(age, 4)} seconds.`
    get('sl-evidence-meta').textContent = `Band ${c.band} · focus ${c.focus}/2 · reading ${Math.floor(age / 4) + 1}`
    updateReferences(c.band)
    const comparison = KINDS.find(kind => kind === reference.value)
    const nextKey = `${c.id}:${c.band}:${c.focus}:${Math.floor(age / 4)}:${comparison}`
    if (profileKey !== nextKey) {
      profiles.innerHTML = VIEWS.map(view => {
        const values = observe(shift!, c, view)
        const label = VIEW_LABELS[view] + (comparison ? `; dashed reference: ${REFERENCE[comparison].name}` : '')
        return `<figure><figcaption>${VIEW_LABELS[view]}</figcaption>${profileSvg(values, label, comparison ? cleanProfile(comparison, view, c.band) : undefined)}</figure>`
      }).join('')
      profileKey = nextKey
    }
  }

  setup.addEventListener('submit', event => {
    event.preventDefault()
    if (!setup.reportValidity()) return
    stopClock()
    shift = createShift(seed.valueAsNumber)
    practice = practiceInput.checked
    selected = 0
    profileKey = ''
    contacts.replaceChildren()
    buttons.clear()
    setup.hidden = true
    board.hidden = false
    results.hidden = true
    status.textContent = practice ? 'Practice shift started. Use Wait 4 seconds to advance.' : 'Shift started. Select a contact and compare its returns.'
    render()
    startClock()
    buttons.get(selected)?.focus()
  })
  pause.addEventListener('click', () => {
    if (!shift || shift.status === 'finished') return
    shift.status = shift.status === 'running' ? 'paused' : 'running'
    status.textContent = shift.status === 'paused' ? 'Paused. Inspect the reference library, then resume when ready.' : 'Shift resumed.'
    startClock()
    render()
  })
  wait.addEventListener('click', () => { if (shift && practice) { advance(shift, 4); render() } })
  reference.addEventListener('change', render)
  focus.addEventListener('click', () => {
    if (shift && focusContact(shift, selected)) { render(); status.textContent = `Focused scan received for contact ${selected}. Less noise, same signal.` }
  })
  band.addEventListener('click', () => {
    if (shift && changeBand(shift, selected)) { render(); status.textContent = 'Band changed. Reference cards now show the same band.' }
  })
  const report = (kind: Kind | null): void => {
    if (!shift) return
    const level = confidence.value
    if (level !== 'tentative' && level !== 'likely' && level !== 'certain') return
    const id = selected
    if (!fileReport(shift, selected, kind, level)) return
    render()
    if (shift.status !== 'finished') status.textContent = `Report filed for contact ${id}. Its identity will be revealed after the shift.`
  }
  get<HTMLFormElement>('sl-report').addEventListener('submit', event => {
    event.preventDefault()
    const kind = KINDS.find(k => k === answer.value)
    if (kind) report(kind)
  })
  unresolved.addEventListener('click', () => report(null))
  get('sl-end').addEventListener('click', () => { if (shift) { finishShift(shift); render() } })
  get('sl-again').addEventListener('click', () => {
    stopClock()
    shift = null
    results.hidden = true
    setup.hidden = false
    seed.value = String(Number(seed.value) % 999999 + 1)
    status.textContent = 'Ready for another shift. Reuse a seed to replay the same contacts.'
    get('sl-start').focus()
  })
  const suspend = (): void => {
    if (shift?.status !== 'running') return
    shift.status = 'paused'
    stopClock()
    status.textContent = 'Shift paused while you were away. Press Resume to continue.'
    render()
  }
  document.addEventListener('visibilitychange', () => { if (document.hidden) suspend() })
  window.addEventListener('pagehide', suspend)
  practiceInput.checked = matchMedia('(prefers-reduced-motion: reduce)').matches
  get<HTMLButtonElement>('sl-start').disabled = false
}

install()
